import * as fromFeedActions from './actions/feed.actions';
import * as fromReducers from './reducers';

import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { AppState } from '../reducers';
import { Feed } from './models/core.models';
import { FeedService } from './services/feed.service';
import { Injectable } from '@angular/core';
import { LoggerService } from '@services/logger.service';
import { augmentObjectWithIconAndImage } from '../shared/utils/object-utils';

@Injectable({ providedIn: 'root' })
export class FeedResolver implements Resolve<Feed | null> {
  constructor(
    private store: Store<AppState>,
    private feedService: FeedService,
    private logger: LoggerService,
    private router: Router,
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<Feed | null> {
    const feedId = route.paramMap.get('feedId');
    if (!feedId) {
      this.logger.error('No feedId in route');
      return of(null);
    }

    return this.loadFeedDirectly(feedId);
  }

  private loadFeedDirectly(feedId: string): Observable<Feed | null> {
    return this.store.pipe(
      // grab the current user once
      select(fromReducers.selectUser),
      take(1),
      // switch into the HTTP call
      switchMap((user) =>
        this.feedService.getFeed(user.id, feedId).pipe(
          // 1) transform the raw Feed
          filter((feed) => !!feed),
          map((feed) => augmentObjectWithIconAndImage(feed)), // returns Feed

          // 2) dispatch the success into NgRx
          tap((feed) =>
            this.store.dispatch(
              fromFeedActions.loadFeedSuccess({ feed, setCurrentFeed: true }),
            ),
          ),
        ),
      ),

      // if anything errors (store-select never will, but HTTP can),
      // catch it here and emit null
      catchError((err) => {
        this.logger.error('Failed to load feed', err);
        return of<null>(null);
      }),

      // finish the whole stream after the first emission
      take(1),
    );
  }
}
